import is from '@sindresorhus/is';

import { resizedWidthCssValue } from './tile-resize';
import type { ResizedWidth } from './types';

// Set on the surface while a width changes, so no tile animates its width under the pointer.
const TILE_RESIZING_ATTRIBUTE = 'data-tile-resizing';

// One width change of one tile, from the first written width until the committed one.
export type ResizeSession = {
    readonly anchorRight: number;
    readonly box: HTMLElement;
    readonly container: HTMLElement;
    readonly isPinnedToEnd: boolean;
    readonly previousValue: string;
    readonly startWidth: number;
    readonly varName: string;
};

export const startResizeSession = (container: HTMLElement, box: HTMLElement, varName: string): ResizeSession => {
    const rect = box.getBoundingClientRect();
    container.setAttribute(TILE_RESIZING_ATTRIBUTE, '');

    return {
        anchorRight: rect.right,
        box,
        container,
        isPinnedToEnd: container.scrollLeft >= container.scrollWidth - container.clientWidth - 1,
        previousValue: container.style.getPropertyValue(varName),
        startWidth: rect.width,
        varName,
    };
};

const writeResizeValue = (session: ResizeSession, value: string | undefined): void => {
    const { container, varName } = session;

    if (is.undefined(value) || value === '') {
        container.style.removeProperty(varName);
    } else {
        container.style.setProperty(varName, value);
    }
};

// Writes the width without touching the scroll position.
export const writeResizeWidth = (session: ResizeSession, width: ResizedWidth | undefined): void => {
    writeResizeValue(
        session,
        is.undefined(width) ? undefined : resizedWidthCssValue(is.number(width) ? Math.round(width) : width),
    );
};

// Keeps the view steady while the width changes: a panel holds its right edge in place, so Main
// gives way first and the surface scrolls once Main is at its minimum. A surface parked at its end
// stays there, which keeps the tiles beside a sticky dock in view.
const keepViewSteady = (session: ResizeSession): void => {
    const { container } = session;

    if (session.isPinnedToEnd) {
        container.scrollLeft = container.scrollWidth;
        return;
    }

    container.scrollLeft += session.box.getBoundingClientRect().right - session.anchorRight;
};

export const applyResizeWidth = (session: ResizeSession, width: ResizedWidth | undefined): void => {
    writeResizeWidth(session, width);
    keepViewSteady(session);
};

// Reading the layout first applies the final width while transitions are still off; only then may
// tiles animate again.
export const endResizeSession = (session: ResizeSession): void => {
    session.box.getBoundingClientRect();
    session.container.removeAttribute(TILE_RESIZING_ATTRIBUTE);
};

export const cancelResize = (session: ResizeSession): void => {
    writeResizeValue(session, session.previousValue);
    keepViewSteady(session);
    endResizeSession(session);
};

// Returns the width to remember. A tile its own width held back goes back to its default, one its
// maximum held back stays at the maximum on any screen.
export const commitResize = (session: ResizeSession, desiredWidth: number): ResizedWidth | undefined => {
    applyResizeWidth(session, desiredWidth);
    const width = Math.round(session.box.getBoundingClientRect().width);
    const committedWidth = desiredWidth < width - 1 ? undefined : desiredWidth > width + 1 ? 'max' : width;
    applyResizeWidth(session, committedWidth);
    endResizeSession(session);

    return committedWidth;
};

export type ResizeRange = {
    readonly max: number;
    readonly min: number;
    readonly now: number;
};

// The tile's current width with the narrowest and widest widths it can take right now. Both ends
// are laid out for one read each and the start state is put back before the next paint.
export const measureResizeRange = (session: ResizeSession): ResizeRange => {
    const { box, container } = session;
    const scrollLeft = container.scrollLeft;

    writeResizeWidth(session, 0);
    const min = box.getBoundingClientRect().width;
    writeResizeWidth(session, 'max');
    const max = box.getBoundingClientRect().width;
    writeResizeValue(session, session.previousValue);
    container.scrollLeft = scrollLeft;
    endResizeSession(session);

    return { max: Math.round(max), min: Math.round(min), now: Math.round(session.startWidth) };
};
